import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { type SearchPdpsInfiniteQueryVariables, useComplexSearchPdpsMutation } from '@vizit/api';
import type { ConfigFieldCondition, PdpLogicalCondition, PdpView } from '@vizit/api';

interface SearchPdpsPage {
  data: {
    products: PdpView[];
    totalCount: number;
  };
  nextCursor: number | undefined;
}

type QueryCondition = ConfigFieldCondition | PdpLogicalCondition;

/**
 * Builds query conditions from the search variables.
 *
 * @param variables - The variables for the query
 * @returns The query conditions
 *
 * @example
 * ```tsx
 * const conditions = buildQueryConditions({
 *   brand: ['brand1', 'brand2'],
 *   product_category_id: ['category1', 'category2'],
 *   listing_score_level: ['high', 'very_high'],
 *   search: 'search term',
 * });
 * ```
 */
function buildQueryConditions(variables: SearchPdpsInfiniteQueryVariables): QueryCondition[] {
  const { brand, product_category_id, category_id, listing_score_level, search, retailer, experimentOnly } = variables;

  const conditions: QueryCondition[] = [];

  // Filter conditions
  if (brand?.length && brand.length > 0) {
    conditions.push({ comparator: 'in', field: 'brand', value: brand });
  }
  if (category_id?.length && category_id.length > 0) {
    conditions.push({ comparator: 'in', field: 'category_id', value: category_id });
  } else if (product_category_id?.length && product_category_id.length > 0) {
    conditions.push({ comparator: 'in', field: 'product_category_id', value: product_category_id });
  }
  if (listing_score_level?.length && listing_score_level.length > 0) {
    conditions.push({ comparator: 'in', field: 'listing_score_level', value: listing_score_level });
  }
  if (retailer?.length && retailer.length > 0) {
    conditions.push({ comparator: 'in', field: 'retailer', value: retailer });
  }

  // Unified search: matches name OR item_id (ASIN)
  if (search) {
    conditions.push({
      operator: 'or',
      conditions: [
        { comparator: 'ilike', field: 'name', value: variables.search },
        { comparator: 'ilike', field: 'item_id', value: variables.search },
      ],
    });
  }

  // Show only products whose score changed since ingest.
  // 'score_change' joins the filterable field union once the backend adds it and
  // `pnpm generate` runs — drop the cast then.
  if (experimentOnly) {
    conditions.push({ comparator: '!=', field: 'score_change' as ConfigFieldCondition['field'], value: 0 });
  }

  return conditions;
}

const queryKey = (variables: SearchPdpsInfiniteQueryVariables) => ['SearchPdpsPaginated', variables];

/**
 * Paginated search PDPs query using the complex search mutation.
 * NOTE: MUST be deduplicated by the user of the hook
 *
 * @param variables - The variables for the query
 * @returns The result of the query
 *
 * @example
 * ```tsx
 * const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useSearchPdpsInfiniteQuery({
 *   workspace_id: 'portfolio-123',
 *   organization_id: 'organization-123',
 *   limit: 20,
 *   sort_by: 'created_at',
 *   sort_direction: 'desc',
 * });
 */
function useSearchPdpsInfiniteQuery(
  variables: SearchPdpsInfiniteQueryVariables,
): UseInfiniteQueryResult<InfiniteData<SearchPdpsPage, number>, Error> {
  const limit = variables.limit ?? 20;

  return useInfiniteQuery({
    queryKey: queryKey(variables),
    queryFn: async ({ pageParam = 0 }) => {
      if (!variables.workspace_id) {
        throw new Error('Portfolio ID is required');
      }

      const conditions = buildQueryConditions(variables);

      const { data: result, total_count: totalCount } = await useComplexSearchPdpsMutation.fetcher({
        workspace_id: variables.workspace_id,
        order: variables.sort_by ? [{ field: variables.sort_by, direction: variables.sort_direction ?? 'asc' }] : [],
        pagination: { offset: pageParam as number, limit },
        query: conditions.length > 0 ? { operator: 'and', conditions } : undefined,
      })();

      const hasData = result && result.length > 0;

      return {
        data: {
          products: result,
          totalCount,
        },
        // overlap each page by 10% to reduce gaps when underlying data changes
        // Note: MUST be deduplicated by the user of the hook
        nextCursor: hasData ? (pageParam as number) + Math.ceil(limit * 0.9) : undefined,
      };
    },
    initialPageParam: 0,
    enabled: !!variables.workspace_id,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    placeholderData: keepPreviousData,
  });
}

useSearchPdpsInfiniteQuery.getKey = (variables: SearchPdpsInfiniteQueryVariables) => queryKey(variables);

export { useSearchPdpsInfiniteQuery, buildQueryConditions };
